import React, { useState, useContext } from 'react';
import { 
  Container, Typography, Box, Button, Dialog, 
  DialogActions, Table, TableBody, TableCell, 
  TableHead, TableRow, IconButton, Chip, CircularProgress, Alert,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Switch, FormControlLabel
} from '@mui/material';
import { AnimatedPaper, StyledTableContainer, StyledButton, tableRowHoverStyles, iconButtonStyles, StyledDialogTitle, StyledDialogContent } from '../components/StyledComponents';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery, useMutation, gql } from '@apollo/client';
import AuthContext from '../contexts/AuthContext';

// GraphQL queries et mutations
const GET_ABSENCE_TYPES = gql`
  query GetAbsenceTypes {
    absenceTypes {
      id
      code
      name
      description
      isWorkingDay
    }
  }
`;

const CREATE_ABSENCE_TYPE = gql`
  mutation CreateAbsenceType($code: String!, $name: String!, $description: String, $isWorkingDay: Boolean) {
    createAbsenceType(
      code: $code
      name: $name
      description: $description
      isWorkingDay: $isWorkingDay
    ) {
      success
      message
      absenceType {
        id
        code
        name
        description
        isWorkingDay
      }
    }
  }
`;

const UPDATE_ABSENCE_TYPE = gql`
  mutation UpdateAbsenceType($id: ID!, $code: String, $name: String, $description: String, $isWorkingDay: Boolean) {
    updateAbsenceType(
      id: $id
      code: $code
      name: $name
      description: $description
      isWorkingDay: $isWorkingDay
    ) {
      success
      message
      absenceType {
        id
        code
        name
        description
        isWorkingDay
      }
    }
  }
`;

const DELETE_ABSENCE_TYPE = gql`
  mutation DeleteAbsenceType($id: ID!) {
    deleteAbsenceType(id: $id) {
      success
      message
    }
  }
`;

function AbsenceTypes() {
  const { user } = useContext(AuthContext);
  
  // État pour la recherche
  const [searchQuery, setSearchQuery] = useState('');
  
  // États pour les dialogues
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  // États pour les formulaires
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    isWorkingDay: true
  });
  
  const [selectedType, setSelectedType] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Queries
  const { data: absenceTypesData, loading: absenceTypesLoading, refetch: refetchAbsenceTypes } = useQuery(GET_ABSENCE_TYPES);

  // Mutations
  const [createAbsenceType, { loading: createLoading }] = useMutation(CREATE_ABSENCE_TYPE);
  const [updateAbsenceType, { loading: updateLoading }] = useMutation(UPDATE_ABSENCE_TYPE);
  const [deleteAbsenceType, { loading: deleteLoading }] = useMutation(DELETE_ABSENCE_TYPE);

  // Filtrer les types d'absence
  const filteredTypes = absenceTypesData?.absenceTypes?.filter(type => {
    const searchLower = searchQuery.toLowerCase();
    return (
      type.code.toLowerCase().includes(searchLower) ||
      type.name.toLowerCase().includes(searchLower) ||
      type.description.toLowerCase().includes(searchLower)
    );
  }) || [];

  // Gestionnaires d'événements
  const handleOpenCreateDialog = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      isWorkingDay: true
    });
    setErrorMessage('');
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const handleOpenEditDialog = (type) => {
    setSelectedType(type);
    setFormData({
      code: type.code,
      name: type.name,
      description: type.description,
      isWorkingDay: type.isWorkingDay
    });
    setErrorMessage('');
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedType(null);
  };

  const handleOpenDeleteDialog = (type) => {
    setSelectedType(type);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedType(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (openCreateDialog) {
        const result = await createAbsenceType({
          variables: {
            code: formData.code,
            name: formData.name,
            description: formData.description,
            isWorkingDay: formData.isWorkingDay
          }
        });

        if (result.data.createAbsenceType.success) {
          handleCloseCreateDialog();
          refetchAbsenceTypes();
          alert('Type d\'absence créé avec succès');
        } else {
          setErrorMessage(result.data.createAbsenceType.message);
        }
      } else {
        const result = await updateAbsenceType({
          variables: {
            id: selectedType.id,
            code: formData.code,
            name: formData.name,
            description: formData.description,
            isWorkingDay: formData.isWorkingDay
          }
        });

        if (result.data.updateAbsenceType.success) {
          handleCloseEditDialog();
          refetchAbsenceTypes();
          alert('Type d\'absence mis à jour avec succès');
        } else {
          setErrorMessage(result.data.updateAbsenceType.message);
        }
      }
    } catch (error) {
      setErrorMessage('Erreur: ' + error.message);
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deleteAbsenceType({
        variables: { id: selectedType.id }
      });

      if (result.data.deleteAbsenceType.success) {
        handleCloseDeleteDialog();
        refetchAbsenceTypes();
        alert('Type d\'absence supprimé avec succès');
      } else {
        alert('Erreur: ' + result.data.deleteAbsenceType.message);
      }
    } catch (error) {
      alert('Erreur lors de la suppression: ' + error.message);
    }
  };

  if (absenceTypesLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Types d'Absence
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Rechercher"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {(user?.role === 'ADMIN' || user?.role === 'RH') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            sx={{ mb: 2 }}
          >
            Ajouter un type d'absence
          </Button>
        )}
      </Box>

      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Jour ouvrable</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTypes.map((type, index) => (
              <TableRow 
                key={type.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                  ...tableRowHoverStyles
                }}
              >
                <TableCell>
                  <Chip 
                    label={type.code} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body1" fontWeight="medium">
                    {type.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {type.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={type.isWorkingDay ? 'Oui' : 'Non'} 
                    color={type.isWorkingDay ? 'success' : 'default'} 
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', '& > *': { display: 'inline-block', marginRight: '4px' } }}>
                  {/* Boutons d'action */}
                  {(user?.role === 'ADMIN' || user?.role === 'RH') && (
                    <>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditDialog(type)}
                        sx={iconButtonStyles}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(type)}
                        sx={iconButtonStyles}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {filteredTypes.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            {searchQuery ? 'Aucun type trouvé' : 'Aucun type d\'absence'}
          </Typography>
        </Box>
      )}

      {/* Dialogue de création/modification */}
      <Dialog open={openCreateDialog || openEditDialog} onClose={openCreateDialog ? handleCloseCreateDialog : handleCloseEditDialog} maxWidth="md" fullWidth>
        <StyledDialogTitle>
          {openCreateDialog ? 'Nouveau type d\'absence' : 'Modifier le type d\'absence'}
        </StyledDialogTitle>
        <form onSubmit={handleSubmit}>
          <StyledDialogContent>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Code"
                required
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                helperText="Code court (ex: S, V, M, P, C, Co, W, O, H1, H2)"
              />
              
              <TextField
                fullWidth
                label="Nom"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                helperText="Nom complet du type d'absence"
              />
              
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                helperText="Description détaillée (optionnelle)"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isWorkingDay}
                    onChange={(e) => setFormData({ ...formData, isWorkingDay: e.target.checked })}
                  />
                }
                label="Jour ouvrable"
              />
            </Box>
          </StyledDialogContent>
          <DialogActions>
            <Button onClick={openCreateDialog ? handleCloseCreateDialog : handleCloseEditDialog}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={createLoading || updateLoading}>
              {createLoading || updateLoading ? <CircularProgress size={20} /> : (openCreateDialog ? 'Créer' : 'Modifier')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialogue de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <StyledDialogTitle>Confirmer la suppression</StyledDialogTitle>
        <StyledDialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le type d'absence "{selectedType?.name}" ?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Cette action est irréversible.
          </Typography>
        </StyledDialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AbsenceTypes; 